/* AMS DIVISION v3 · POSTER WALL — wall.js: wetness, your-size memory, pocket stubs, strip flight, peel,
   views, paste more, paste pass, countdown, look multi-add, layers, code strips and the share card.
   Vanilla, no dependencies. Every listener is delegated once, so section reloads never stack handlers. */
(() => {
  const d = document;
  const w = window;
  const root = d.documentElement;
  const theme = w.theme || {};
  const cfg = theme.settings || {};
  const S = theme.strings || {};
  const $ = (s, c = d) => c.querySelector(s);
  const $$ = (s, c = d) => Array.from(c.querySelectorAll(s));
  const all = (s, c) => (c.matches && c.matches(s) ? [c] : []).concat($$(s, c));
  const ams = () => w.AMS || null;
  const calm = () => root.dataset.motion === 'calm';
  const design = () => Boolean(w.Shopify && w.Shopify.designMode);
  const mobile = () => w.matchMedia('(max-width: 749px)').matches;
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const fill = (s, m) => Object.keys(m).reduce((t, k) => t.split(`__${k}__`).join(m[k]), String(s || ''));
  const say = (t) => ams() && t && ams().announce(t);
  const emit = (el, name, detail) => el.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));

  /* ---------- storage: consent-gated (Danish Cookie Order / ePrivacy) ----------
     The theme's own keys always live in memory for the current page. They are written to the browser
     only while Shopify's Customer Privacy API says preferencesProcessingAllowed(); no API (preview,
     blocked script, load error) means nothing is written. Reading and deleting are always allowed. */
  const KEYS = { 'ams.size': 'localStorage', 'ams.view': 'localStorage', 'ams-pasted': 'sessionStorage' };
  const mem = {};
  const raw = (key, val) => {
    try {
      const s = w[KEYS[key]];
      if (val === undefined) return s.getItem(key);
      if (val === null) s.removeItem(key);
      else s.setItem(key, val);
    } catch (e) {}
    return null;
  };
  const privacy = () => (w.Shopify && w.Shopify.customerPrivacy) || null;
  const allowed = () => {
    try {
      const p = privacy();
      return Boolean(p && typeof p.preferencesProcessingAllowed === 'function' && p.preferencesProcessingAllowed());
    } catch (e) {
      return false;
    }
  };
  const store = (key, val) => {
    if (val === undefined) {
      if (!(key in mem)) mem[key] = raw(key);
      return mem[key];
    }
    mem[key] = val;
    /* a refused write deletes the old copy, so a stale stored value can never outlive a newer choice;
       if consent arrives later, consent(true) persists the in-memory value */
    if (val === null || !allowed()) raw(key, null);
    else raw(key, val);
    return null;
  };
  /* consent given: persist what is in memory; consent refused or withdrawn: delete every key */
  const consent = (ok) => Object.keys(KEYS).forEach((k) => (ok ? mem[k] != null && raw(k, mem[k]) : raw(k, null)));
  const watchConsent = () => {
    d.addEventListener('visitorConsentCollected', (e) => consent(Boolean(e.detail && e.detail.preferencesAllowed)));
    const settle = () => privacy() && consent(allowed());
    if (privacy()) {
      settle();
      return;
    }
    try {
      if (w.Shopify && typeof w.Shopify.loadFeatures === 'function')
        w.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => !err && settle());
    } catch (e) {}
  };

  const sheetOf = (p) => $(':scope > .poster__sheet', p);
  const slotOf = (p) => $(':scope > [data-under-slot]', p);

  /* ---------- lifecycle registry: observers and timers per element ---------- */
  const tasks = new Set();
  const task = (el, stop) => tasks.add({ el, stop });
  const destroy = (scope = d) =>
    tasks.forEach((t) => {
      if (scope === d || scope === t.el || (scope.contains && scope.contains(t.el))) {
        t.stop();
        tasks.delete(t);
      }
    });

  /* ---------- wetness: the store dries between drops ---------- */
  const wet = (scope) =>
    all('[data-poster][data-pasted]', scope).forEach((p) => {
      const t = Number(p.dataset.pasted);
      if (!t) return;
      let v = clamp(1 - (Date.now() - t * 1000) / ((Number(cfg.dryDays) || 14) * 864e5), 0, 1);
      if (p.hasAttribute('data-wet-floor')) v = Math.max(v, 0.15);
      if (cfg.wet === false) v = 0;
      p.style.setProperty('--wet', v.toFixed(2));
    });

  /* ---------- YOURS: remembered size ---------- */
  const ALIAS = { XSMALL: 'XS', SMALL: 'S', MEDIUM: 'M', LARGE: 'L', XLARGE: 'XL', XXLARGE: 'XXL', '2XL': 'XXL', '3XL': 'XXXL' };
  const norm = (v) => {
    const s = String(v || '').toUpperCase().replace(/[\s-]/g, '');
    return ALIAS[s] || s;
  };
  const sizes = () => {
    try {
      return JSON.parse(store('ams.size')) || {};
    } catch (e) {
      return {};
    }
  };
  const size = {
    get: (g = 'tops') => (cfg.rememberSize === false ? null : sizes()[g] || null),
    set(g = 'tops', v) {
      if (cfg.rememberSize === false) return;
      const o = sizes();
      const val = v ? norm(v) : null;
      if (val) o[g] = val;
      else delete o[g];
      store('ams.size', Object.keys(o).length ? JSON.stringify(o) : null);
      paint(d);
      emit(d, 'wall:size', { group: g, value: val });
    },
    /* "Forget my size": every group (tops and bottoms) and the stored key */
    clear() {
      store('ams.size', null);
      paint(d);
      emit(d, 'wall:size', { group: null, value: null });
    }
  };
  const groupOf = (el) => {
    const f = el.closest('[data-fringe]') || $('[data-fringe]', el.closest('[data-poster]') || el);
    return (f && f.dataset.sizeGroup) || 'tops';
  };
  const stripSize = (s) => s.dataset.size || (s.previousElementSibling && s.previousElementSibling.dataset.size) || '';

  const pocketLabel = (input) => {
    const form = input.closest('[data-quick-pocket]');
    const btn = form && $('[data-pocket-button]', form);
    const lab = btn && $('[data-pocket-label]', btn);
    if (!lab) return;
    const out = input.dataset.available === 'false';
    btn.disabled = out;
    lab.textContent = out ? S.soldOut : fill(S.pocketSize, { SIZE: input.dataset.size });
  };

  /* legend "Selected: M" for hero, look and card fringes (the PDP legend belongs to theme.js) */
  const legend = (input) => {
    const fr = input.closest('[data-fringe]');
    const sel = fr && !fr.closest('[data-product-section]') && $('[data-fringe-selected]', fr);
    if (sel && input.checked) sel.textContent = fill(S.selected, { VALUE: input.dataset.size });
  };

  const preselect = (fr, mine) => {
    if (!mine || $('.fringe__input:checked', fr)) return;
    const i = $$('.fringe__input', fr).find((x) => norm(x.dataset.size) === mine && x.dataset.available !== 'false');
    if (!i) return;
    i.checked = true;
    pocketLabel(i);
    legend(i);
  };

  const chipSync = (chip, o) => {
    const v = cfg.rememberSize === false ? null : o[chip.dataset.group || 'tops'] || null;
    const out = $('[data-size-chip-value]', chip);
    if (out) out.textContent = v || '—';
    $$('[data-size-set]', chip).forEach((b) => b.setAttribute('aria-pressed', String(norm(b.dataset.sizeSet) === v)));
  };

  /* ---------- pocket stubs ---------- */
  let pocket = null;
  const markPocket = (scope) =>
    pocket &&
    all('.fringe__strip[data-variant-ids]', scope).forEach((s) => {
      const on = s.dataset.variantIds.split(',').some((id) => pocket.has(id));
      s.classList.toggle('is-pocketed', on);
      if (on) s.dataset.pocketedLabel = S.inPocket || '';
    });
  const loadPocket = () =>
    ams() &&
    ams()
      .cart.get()
      .then((c) => {
        pocket = new Set((c.items || []).map((i) => String(i.variant_id || i.id)));
        markPocket(d);
      })
      .catch(() => {});

  const paint = (scope) => {
    const o = cfg.rememberSize === false ? {} : sizes();
    all('[data-fringe] .fringe__strip, .poster__avail-size', scope).forEach((s) => {
      const mine = o[groupOf(s)];
      const on = Boolean(mine) && norm(stripSize(s)) === mine;
      s.classList.toggle('is-yours', on);
      if (on) s.dataset.yoursLabel = S.yours || '';
    });
    all('[data-preselect] [data-fringe]', scope).forEach((f) => preselect(f, o[f.dataset.sizeGroup || 'tops']));
    all('[data-size-chip]', scope).forEach((c) => chipSync(c, o));
    markPocket(scope);
  };

  const chipClose = (chip, focus) => {
    const p = $('.size-chip__pop', chip);
    const b = $('.size-chip__btn', chip);
    if (!p || p.hidden) return;
    p.hidden = true;
    b.setAttribute('aria-expanded', 'false');
    if (focus) b.focus();
  };

  /* ---------- strip flight: torn strip into the pocket ---------- */
  const flights = new Map();
  const target = () => {
    const icon = $$('[data-pocket-icon]').find((i) => i.getClientRects().length);
    const r = icon && icon.getBoundingClientRect();
    return r && r.bottom > 0 ? { icon, x: r.left + r.width / 2, y: r.top + r.height / 2 } : { icon, x: w.innerWidth - 36, y: 24 };
  };
  const fly = (el) => {
    const t = target();
    const r = el.getBoundingClientRect();
    const dx = t.x - (r.left + r.width / 2);
    const dy = t.y - (r.top + r.height / 2);
    const a = el.animate(
      [
        { transform: 'translate(0,0) rotate(4deg) scale(1)' },
        { transform: `translate(${dx * 0.5}px,${dy * 0.5 - 70}px) rotate(9deg) scale(.66)`, offset: 0.5 },
        { transform: `translate(${dx}px,${dy}px) rotate(14deg) scale(.3)`, opacity: 0.35 }
      ],
      { duration: 520, easing: 'cubic-bezier(.3,.7,.2,1)', fill: 'forwards' }
    );
    a.onfinish = a.oncancel = () => {
      el.remove();
      if (!t.icon) return;
      t.icon.classList.add('is-gulp');
      setTimeout(() => t.icon.classList.remove('is-gulp'), 180);
    };
  };
  const dropFlight = (src) => {
    const f = flights.get(src);
    if (f) f.els.forEach((el) => el.remove());
    flights.delete(src);
  };

  /* ---------- peel: to the previous layer ---------- */
  let peeling = null;
  let drag = null;
  const hydrate = (poster) => {
    const slot = slotOf(poster);
    const tpl = $(':scope > template[data-under]', poster);
    if (slot && tpl && !slot.childElementCount) {
      slot.append(tpl.content.cloneNode(true));
      /* hydration is peel intent: fetch the archive image now, not when the curl uncovers it */
      $$('img[loading="lazy"]', slot).forEach((i) => (i.loading = 'eager'));
      init(slot);
    }
    return slot;
  };
  const fade = (poster, v) =>
    $$(':scope > .poster__sheet > :not(.poster__media):not(.poster__attach)', poster).forEach((el) => {
      el.style.opacity = v >= 1 ? '' : v.toFixed(2);
    });

  /* CSS fallback: a clip-path fold whose line is the perpendicular bisector of corner → pointer. */
  const cssPeel = (poster) => {
    const sheet = sheetOf(poster);
    const W = sheet.offsetWidth;
    const H = sheet.offsetHeight;
    const r = sheet.getBoundingClientRect();
    let p = 0;
    let raf = 0;
    let last = [1, 1];
    const set = (dx, dy) => {
      dx = Math.max(dx, 1);
      dy = Math.max(dy, 1);
      const q = (dx * dx + dy * dy) / 2;
      poster.style.setProperty('--fx', `${Math.min(q / dx, W * 2).toFixed(1)}px`);
      poster.style.setProperty('--fy', `${Math.min(q / dy, H * 2).toFixed(1)}px`);
      p = clamp(Math.hypot(dx, dy) / (W * 1.1), 0, 1);
      poster.style.setProperty('--peel', p.toFixed(3));
      return p;
    };
    const clear = () => {
      cancelAnimationFrame(raf);
      poster.classList.remove('is-peeling', 'is-falling');
      ['--fx', '--fy', '--peel'].forEach((k) => poster.style.removeProperty(k));
    };
    const tear = () =>
      new Promise((res) => {
        poster.classList.add('is-falling');
        setTimeout(() => {
          poster.classList.add('is-torn');
          clear();
          res(true);
        }, 700);
      });
    const back = () =>
      new Promise((res) => {
        let s = 1;
        let v = 0;
        let t0 = performance.now();
        const step = (now) => {
          const dt = Math.min(0.032, (now - t0) / 1000);
          t0 = now;
          v += (-520 * s - 26 * v) * dt;
          s += v * dt;
          if (s <= 0.01) {
            clear();
            res(false);
            return;
          }
          set(last[0] * s, last[1] * s);
          raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
      });
    poster.classList.add('is-peeling');
    set(1, 1);
    return {
      move(x, y) {
        last = [clamp(r.right - x, 1, W * 1.6), clamp(y - r.top, 1, H * 1.6)];
        return set(last[0], last[1]);
      },
      end: () => (p >= 0.5 ? tear() : back()),
      auto: () =>
        new Promise((res) => {
          const t0 = performance.now();
          const step = (now) => {
            const k = clamp((now - t0) / 900, 0, 1);
            const e = 1 - Math.pow(1 - k, 3);
            last = [W * 0.62 * e + 1, H * 0.44 * e + 1];
            set(last[0], last[1]);
            if (k < 1) raf = requestAnimationFrame(step);
            else tear().then(res);
          };
          raf = requestAnimationFrame(step);
        }),
      cancel: clear
    };
  };

  /* Calm mode: the peel becomes a 200ms crossfade. */
  const calmPeel = (poster) => {
    const go = () =>
      new Promise((res) => {
        poster.classList.add('is-peeling');
        const a = sheetOf(poster).animate([{ opacity: 1 }, { opacity: 0 }], { duration: 200, fill: 'forwards' });
        a.onfinish = () => {
          a.cancel();
          res(true);
        };
      });
    return { move: () => 0, end: go, auto: go, cancel() {} };
  };

  const startPeel = (poster, x, y) => {
    if (peeling || !poster) return null;
    hydrate(poster);
    let h = null;
    if (calm()) h = calmPeel(poster);
    else if (w.Klister && w.Klister.eligible) {
      try {
        h = w.Klister.peelStart(poster, { x, y });
      } catch (e) {
        h = null;
      }
    }
    h = h || cssPeel(poster);
    peeling = poster;
    return h;
  };

  const finish = (poster, torn) => {
    peeling = null;
    fade(poster, 1);
    if (!torn) {
      poster.classList.remove('is-peeling');
      emit(poster, 'wall:peel', { poster, torn: false });
      return;
    }
    poster.classList.add('is-torn');
    poster.classList.remove('is-peeling', 'is-falling');
    const slot = slotOf(poster);
    slot.classList.add('is-revealed');
    slot.inert = false;
    slot.removeAttribute('inert');
    slot.removeAttribute('aria-hidden');
    if (calm()) slot.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200 });
    const back = $(':scope > [data-paste-back]', poster);
    if (back) back.hidden = false;
    const link = $('.poster__link', slot);
    const tape = $('.tape--layer', slot);
    if (link) link.focus({ preventScroll: true });
    say(fill(S.previousLayerAnnounce, { LAYER: tape ? tape.textContent.trim() : '', TITLE: link ? link.textContent.trim() : '' }));
    emit(poster, 'wall:peel', { poster, torn: true });
  };

  const pasteBack = (poster) => {
    const sheet = sheetOf(poster);
    const slot = slotOf(poster);
    if (sheet.getAnimations) sheet.getAnimations().forEach((a) => a.cancel());
    poster.classList.remove('is-torn', 'is-falling', 'is-peeling', 'is-gl-peeling');
    slot.classList.remove('is-revealed');
    slot.inert = true;
    slot.setAttribute('aria-hidden', 'true');
    const back = $(':scope > [data-paste-back]', poster);
    if (back) back.hidden = true;
    if (calm()) sheet.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200 });
    else {
      poster.classList.add('is-pasted-back');
      setTimeout(() => poster.classList.remove('is-pasted-back'), 500);
    }
    const ear = $(':scope > [data-peel]', poster);
    if (ear) ear.focus({ preventScroll: true });
    emit(poster, 'wall:peel', { poster, torn: false });
  };

  /* ---------- views: Wall / Stack ---------- */
  const hostOf = (el) => el.closest('.shopify-section') || el.closest('section') || d;
  const setView = (wall, view, save) => {
    wall.classList.toggle('wall--stack', view === 'stack');
    $$('[data-view-toggle] [data-view]', hostOf(wall)).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.view === view)));
    if (!save) return;
    store('ams.view', view);
    emit(wall, 'wall:view', { view });
  };
  const initView = (wall) => {
    if (wall.dataset.wallReady) return;
    wall.dataset.wallReady = '1';
    const saved = $('[data-view-toggle]', hostOf(wall)) ? store('ams.view') : null;
    const v = saved || (mobile() ? wall.dataset.defaultViewMobile : wall.dataset.defaultViewDesktop);
    if (v) setView(wall, v === 'stack' ? 'stack' : 'wall');
  };

  /* ---------- paste more (Section Rendering API) ---------- */
  const pasteMore = (btn) => {
    const host = hostOf(btn);
    const wall = $('[data-wall]', host);
    const items = wall && $('[data-wall-items]', wall);
    const url = btn.dataset.nextUrl;
    const holder = btn.closest('[data-section-id]');
    const sid = (wall && wall.dataset.sectionId) || (holder && holder.dataset.sectionId) || '';
    if (!items || !url || btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=${encodeURIComponent(sid)}`)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = $('[data-wall-items]', doc);
        const nodes = fresh ? Array.from(fresh.children) : [];
        nodes.forEach((n) => items.append(d.adoptNode(n)));
        init(items);
        const next = $('[data-paste-more]', doc);
        if (next && next.dataset.nextUrl) btn.dataset.nextUrl = next.dataset.nextUrl;
        else btn.hidden = true;
        const pag = $('.pagination', doc);
        const cur = $('.wall__more .pagination', host);
        if (cur) {
          if (pag) cur.replaceWith(d.adoptNode(pag));
          else cur.remove();
        }
        const link = btn.hidden && nodes[0] && $('.poster__link', nodes[0]);
        if (link) link.focus({ preventScroll: true });
      })
      .catch(() => {})
      .finally(() => {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      });
  };

  /* ---------- paste pass: a translucent wet band sweeps over, never hides ---------- */
  const canPaste = () => cfg.pasteReveal !== false && !calm() && !design();
  const pass = (el) => {
    if (!canPaste()) return;
    const s = d.createElement('span');
    s.className = 'paste-pass';
    s.setAttribute('aria-hidden', 'true');
    el.append(s);
    el.classList.add('is-wet');
    let done = false;
    const end = () => {
      if (done) return;
      done = true;
      s.remove();
      setTimeout(() => el.classList.remove('is-wet'), 800);
    };
    s.addEventListener('animationend', end);
    setTimeout(end, 1600);
  };
  let wallPassed = false;
  const initPaste = (scope) => {
    all('[data-paste="hero"]', scope).forEach((el) => {
      if (el.dataset.wallReady) return;
      el.dataset.wallReady = '1';
      if (!canPaste() || store('ams-pasted')) return;
      store('ams-pasted', '1');
      pass(el);
    });
    const first = $('[data-paste="wall"]');
    if (wallPassed || !first || !canPaste() || !('IntersectionObserver' in w)) return;
    wallPassed = true;
    const io = new IntersectionObserver(
      (es) => {
        if (!es.some((x) => x.isIntersecting)) return;
        io.disconnect();
        pass(first);
      },
      { rootMargin: '0px 0px -20% 0px' }
    );
    io.observe(first);
    task(first, () => io.disconnect());
  };

  /* ---------- countdown tape ---------- */
  const countdown = (el) => {
    if (el.dataset.wallReady) return;
    el.dataset.wallReady = '1';
    const end = Date.parse(el.dataset.target);
    if (isNaN(end)) return;
    const parts = {};
    $$('[data-cd]', el).forEach((p) => (parts[p.dataset.cd] = p));
    let id = 0;
    const stop = () => {
      clearInterval(id);
      id = 0;
    };
    const tick = () => {
      const s = Math.max(0, Math.floor((end - Date.now()) / 1000));
      const v = { d: Math.floor(s / 86400), h: Math.floor(s / 3600) % 24, m: Math.floor(s / 60) % 60, s: s % 60 };
      Object.keys(parts).forEach((k) => {
        const t = String(v[k] || 0).padStart(2, '0');
        if (parts[k].textContent !== t) parts[k].textContent = t;
      });
      if (s || el.classList.contains('is-ended')) return;
      stop();
      el.classList.add('is-ended');
      emit(el, 'wall:countdown-end', {});
    };
    tick();
    if (el.classList.contains('is-ended')) return;
    const run = () => {
      if (id) return;
      tick();
      id = setInterval(tick, 1000);
    };
    const io = 'IntersectionObserver' in w ? new IntersectionObserver((es) => (es[es.length - 1].isIntersecting ? run() : stop())) : null;
    if (io) io.observe(el);
    else run();
    task(el, () => {
      stop();
      if (io) io.disconnect();
    });
  };

  /* ---------- look: one add for the whole look ---------- */
  const lookItems = (form) =>
    $$('[data-look-piece]', form)
      .map((p) => {
        const c = $('.fringe__input:checked', p);
        return c ? c.value : p.dataset.variant || '';
      })
      .filter(Boolean);
  const lookLabel = (form) => {
    const btn = form && $('[data-look-submit]', form);
    const lab = btn && $('[data-look-label]', btn);
    if (lab) lab.textContent = fill(btn.dataset.label, { COUNT: lookItems(form).length });
  };

  /* ---------- layers: tear down through the seasons ---------- */
  const setLayer = (sec, k) => {
    const walls = $$('[data-layer]', sec);
    const range = $('[data-layer-range]', sec);
    k = clamp(k, 0, walls.length - 1);
    walls.forEach((wl, i) => {
      const on = i === k;
      wl.inert = !on;
      wl.classList.toggle('is-active', on);
      if (on) wl.removeAttribute('aria-hidden');
      else wl.setAttribute('aria-hidden', 'true');
      const was = wl.classList.contains('is-open');
      wl.classList.toggle('is-open', i <= k);
      wl.classList.remove('is-buried');
      const rim = $(`[data-rim="${i}"]`, sec);
      if (rim && i <= k && !was && !calm()) {
        rim.classList.remove('is-tearing');
        void rim.getBoundingClientRect();
        rim.classList.add('is-tearing');
        setTimeout(() => rim.classList.remove('is-tearing'), 700);
      }
    });
    $$('[data-layer-clip]', sec).forEach((c) => c.classList.toggle('is-open', Number(c.dataset.layerClip) <= k));
    $$('[data-layer-stop]', sec).forEach((s, i) => s.classList.toggle('is-active', i === k));
    if (range) range.setAttribute('aria-valuetext', walls[k].dataset.label || '');
    sec.style.setProperty('--layer', k);
    clearTimeout(sec._wallT);
    sec._wallT = setTimeout(() => walls.forEach((wl, i) => wl.classList.toggle('is-buried', i < k)), 700);
  };
  const initLayers = (sec) => {
    if (sec.dataset.wallReady) return;
    sec.dataset.wallReady = '1';
    const range = $('[data-layer-range]', sec);
    setLayer(sec, range ? Number(range.value) || 0 : 0);
    task(sec, () => clearTimeout(sec._wallT));
  };

  /* ---------- code strips ---------- */
  const copy = (text) =>
    navigator.clipboard && w.isSecureContext
      ? navigator.clipboard.writeText(text)
      : new Promise((res, rej) => {
          const t = d.createElement('textarea');
          t.value = text;
          t.setAttribute('readonly', '');
          t.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
          d.body.append(t);
          t.select();
          const ok = d.execCommand && d.execCommand('copy');
          t.remove();
          if (ok) res();
          else rej();
        });
  const tearCode = (strip) => {
    const done = () => {
      strip.classList.add('is-torn');
      say(S.codeInPocket);
      const host = strip.closest('.fringe') || strip.parentNode;
      const old = $('.signup-sheet__toast', host);
      if (old) old.remove();
      const toast = d.createElement('span');
      toast.className = 'sticker sticker--yours signup-sheet__toast';
      toast.setAttribute('aria-hidden', 'true');
      toast.textContent = S.codeInPocket || '';
      toast.style.left = `${strip.offsetLeft + strip.offsetWidth / 2}px`;
      host.append(toast);
      setTimeout(() => toast.remove(), 2400);
    };
    copy(strip.dataset.code || '').then(done, done);
  };

  /* ---------- share card: 1080×1920 torn-strip Story ---------- */
  const loadImg = (src) =>
    new Promise((res) => {
      if (!src) return res(null);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      const t = setTimeout(() => res(null), 2500);
      img.onload = () => {
        clearTimeout(t);
        res(img);
      };
      img.onerror = () => {
        clearTimeout(t);
        res(null);
      };
      img.src = src.replace(/^\/\//, 'https://');
    });
  const jag = (x, y, wd, h, seed) => {
    const p = new Path2D();
    let r = seed;
    const n = () => ((r = (r * 9301 + 49297) % 233280) / 233280);
    p.moveTo(x, y + 6);
    for (let i = 1; i <= 24; i++) p.lineTo(x + (wd * i) / 24, y + n() * 12);
    p.lineTo(x + wd, y + h);
    for (let i = 23; i >= 0; i--) p.lineTo(x + (wd * i) / 24, y + h - n() * 10);
    p.closePath();
    return p;
  };
  /* date stamp matches the Liquid stamps (wall.date_stamp): "27 SEP" in English, "27.09" in Danish */
  const shareDate = () => {
    const t = new Date();
    const p = (n) => String(n).padStart(2, '0');
    if (/^da(-|$)/i.test(root.lang || '')) return p(t.getDate()) + '.' + p(t.getMonth() + 1);
    let m = '';
    try {
      m = t.toLocaleDateString('en-US', { month: 'short' });
    } catch (e) {
      m = p(t.getMonth() + 1);
    }
    return (p(t.getDate()) + ' ' + m).toUpperCase();
  };
  const shareCard = async (btn) => {
    const scope = btn.closest('[data-cart-inner], .cart-page, .shopify-section') || d;
    const lines = $$('.cart-line[data-line-title]', scope).slice(0, 6);
    const extra = $$('.cart-line[data-line-title]', scope).length - lines.length;
    if (!lines.length || btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    const cs = getComputedStyle(root);
    const v = (k, fb) => cs.getPropertyValue(k).trim() || fb;
    const F = { poster: v('--font-poster', 'Impact'), display: v('--font-display', 'Arial Black'), label: v('--font-label', 'Arial Narrow'), mono: v('--font-mono', 'monospace') };
    const C = { wall: v('--hoarding', '#1B1C19'), paper: v('--paper', '#F2EEE3'), ink: v('--ink', '#0E0E0C'), sticker: v('--sticker', '#FFD400'), tape: v('--tape', '#E6D7A3') };
    try {
      await Promise.all([`120px ${F.poster}`, `100px ${F.display}`, `700 44px ${F.label}`].map((f) => d.fonts.load(f)));
    } catch (e) {}
    const imgs = await Promise.all(lines.map((l) => loadImg(l.dataset.lineImage)));
    const cv = d.createElement('canvas');
    cv.width = 1080;
    cv.height = 1920;
    const x = cv.getContext('2d');
    x.fillStyle = C.wall;
    x.fillRect(0, 0, 1080, 1920);
    for (let i = 0; i < 1400; i++) {
      x.fillStyle = `rgba(242,238,227,${(Math.random() * 0.05).toFixed(3)})`;
      x.fillRect(Math.random() * 1080, Math.random() * 1920, 2, 2);
    }
    x.textBaseline = 'alphabetic';
    x.fillStyle = C.paper;
    x.font = `100px ${F.display}`;
    x.fillText('AMS DIVISION', 72, 210, 936);
    x.save();
    x.translate(72, 250);
    x.rotate(-0.02);
    x.fillStyle = C.tape;
    x.fill(jag(0, 0, 520, 64, 7));
    x.fillStyle = C.ink;
    x.font = `700 30px ${F.mono}`;
    x.fillText(`${(S.inPocket || '').toUpperCase()} · ${shareDate()}`, 26, 44, 470);
    x.restore();
    const h = 190;
    lines.forEach((l, i) => {
      const y = 400 + i * (h + 34);
      x.save();
      x.translate(540, y + h / 2);
      x.rotate(((i % 2 ? 1 : -1) * (1.2 + (i % 3) * 0.5) * Math.PI) / 180);
      x.translate(-470, -h / 2);
      x.fillStyle = 'rgba(0,0,0,.35)';
      x.fill(jag(8, 12, 940, h, 11 + i));
      x.fillStyle = C.paper;
      x.fill(jag(0, 0, 940, h, 11 + i));
      x.fillStyle = C.ink;
      for (let k = 12; k < 930; k += 14) x.fillRect(k, 26, 4, 4);
      x.font = `120px ${F.poster}`;
      x.fillText(String(l.dataset.lineSize || '—').toUpperCase(), 34, 160, 190);
      if (imgs[i]) {
        const im = imgs[i];
        const s = Math.max(130 / im.width, 130 / im.height);
        x.save();
        x.beginPath();
        x.rect(776, 44, 130, 130);
        x.clip();
        x.drawImage(im, 841 - (im.width * s) / 2, 109 - (im.height * s) / 2, im.width * s, im.height * s);
        x.restore();
      }
      x.font = `700 44px ${F.label}`;
      x.fillText(String(l.dataset.lineTitle || '').toUpperCase(), 250, 108, imgs[i] ? 500 : 650);
      const pr = $('.cart-line__price', l);
      x.font = `700 36px ${F.mono}`;
      x.fillText(pr ? pr.textContent.replace(/\s+/g, ' ').trim() : '', 250, 160, 500);
      x.restore();
    });
    /* few strips: the first piece hangs below as a big flyer so the Story never looks empty */
    const top = 400 + lines.length * (h + 34) + 70;
    const hero = imgs.find(Boolean);
    if (hero && 1700 - top > 520) {
      const fh = Math.min(1700 - top, 980);
      const fw = Math.round(fh * 0.78);
      x.save();
      x.translate(540, top + fh / 2);
      x.rotate(0.025);
      x.translate(-fw / 2, -fh / 2);
      x.fillStyle = 'rgba(0,0,0,.4)';
      x.fillRect(14, 18, fw, fh);
      x.fillStyle = C.paper;
      x.fillRect(0, 0, fw, fh);
      const ih = fh - 44;
      const iw = fw - 44;
      const s = Math.max(iw / hero.width, ih / hero.height);
      x.save();
      x.beginPath();
      x.rect(22, 22, iw, ih);
      x.clip();
      x.drawImage(hero, 22 + iw / 2 - (hero.width * s) / 2, 22 + ih / 2 - (hero.height * s) / 2, hero.width * s, hero.height * s);
      x.restore();
      x.fillStyle = C.tape;
      x.save();
      x.translate(fw / 2 - 110, -26);
      x.rotate(-0.04);
      x.fill(jag(0, 0, 220, 58, 3));
      x.restore();
      x.restore();
    }
    if (extra > 0) {
      x.save();
      x.translate(820, 400 + lines.length * (h + 34));
      x.rotate(-0.06);
      x.fillStyle = C.sticker;
      x.fillRect(0, 0, 150, 84);
      x.fillStyle = C.ink;
      x.font = `64px ${F.poster}`;
      x.fillText(`+${extra}`, 22, 68, 110);
      x.restore();
    }
    x.fillStyle = C.paper;
    x.font = `700 34px ${F.mono}`;
    x.fillText(w.location.host.toUpperCase(), 72, 1830, 936);
    x.fillStyle = C.sticker;
    x.fillRect(72, 1760, 240, 10);
    const blob = await new Promise((res) => {
      try {
        cv.toBlob(res, 'image/png');
      } catch (e) {
        res(null);
      }
    });
    btn.classList.remove('is-loading');
    if (!blob) return;
    const file = new File([blob], 'ams-division-strip.png', { type: 'image/png' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      navigator.share({ files: [file], title: S.shareStrip }).catch(() => {});
      return;
    }
    const a = d.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = file.name;
    a.hidden = true;
    d.body.append(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(a.href);
      a.remove();
    }, 4000);
  };

  /* ---------- delegated listeners (bound once) ---------- */
  const bind = () => {
    d.addEventListener('change', (e) => {
      const t = e.target;
      if (!t.matches || !t.matches('.fringe__input')) return;
      if (t.dataset.size) size.set(groupOf(t), t.dataset.size);
      pocketLabel(t);
      legend(t);
      const look = t.closest('[data-look-form]');
      if (look) lookLabel(look);
      emit(t, 'wall:tear', { input: t });
    });

    d.addEventListener('input', (e) => {
      const r = e.target.closest && e.target.closest('[data-layer-range]');
      if (r) setLayer(r.closest('[data-layers]'), Number(r.value));
    });

    d.addEventListener('click', (e) => {
      const t = e.target;
      if (!t.closest) return;
      const chipBtn = t.closest('.size-chip__btn');
      if (chipBtn) {
        const pop = $('.size-chip__pop', chipBtn.closest('[data-size-chip]'));
        const open = pop.hidden;
        $$('[data-size-chip]').forEach((c) => chipClose(c));
        if (!open) return;
        pop.hidden = false;
        chipBtn.setAttribute('aria-expanded', 'true');
        const f = $('[aria-pressed="true"]', pop) || $('button', pop);
        if (f) f.focus();
        return;
      }
      const opt = t.closest('[data-size-set], [data-size-clear]');
      if (opt) {
        const chip = opt.closest('[data-size-chip]');
        const val = opt.dataset.sizeSet || null;
        if (val) {
          size.set(chip.dataset.group || 'tops', val);
          say(fill(S.yourSizeSet, { SIZE: norm(val) }));
        } else {
          size.clear();
          say(S.yourSizeCleared);
        }
        chipClose(chip, true);
        return;
      }
      if (!t.closest('.size-chip__pop')) $$('[data-size-chip]').forEach((c) => chipClose(c));

      const ear = t.closest('[data-peel]');
      if (ear && e.detail === 0) {
        const poster = ear.closest('[data-poster]');
        const r = ear.getBoundingClientRect();
        const h = startPeel(poster, r.right - 4, r.top + 4);
        if (h) h.auto().then((torn) => finish(poster, torn));
        return;
      }
      const back = t.closest('[data-paste-back]');
      if (back) {
        pasteBack(back.closest('[data-poster]'));
        return;
      }
      const vb = t.closest('[data-view-toggle] [data-view]');
      if (vb) {
        $$('[data-wall]', hostOf(vb)).forEach((wl) => setView(wl, vb.dataset.view, true));
        return;
      }
      const more = t.closest('[data-paste-more]');
      if (more) {
        pasteMore(more);
        return;
      }
      const lo = t.closest('[data-look-open]');
      if (lo) {
        const dlg = $('dialog[data-look]', lo.closest('[data-look-section]') || d);
        const form = dlg && $('[data-look-form]', dlg);
        if (!form || typeof dlg.showModal !== 'function') return;
        const o = sizes();
        $$('[data-look-piece] [data-fringe]', form).forEach((f) => preselect(f, cfg.rememberSize === false ? null : o[f.dataset.sizeGroup || 'tops']));
        lookLabel(form);
        const err = $('[data-look-error]', dlg);
        if (err) err.hidden = true;
        dlg._opener = lo;
        dlg.showModal();
        const piece = lo.dataset.lookOpen && $(`[data-look-piece="${lo.dataset.lookOpen}"]`, dlg);
        if (piece) {
          const f = $('.fringe__input:checked', piece) || $('.fringe__input', piece);
          piece.scrollIntoView({ block: 'center' });
          if (f) f.focus({ preventScroll: true });
        }
        if (!dlg.dataset.wallReady) {
          dlg.dataset.wallReady = '1';
          dlg.addEventListener('close', () => dlg._opener && dlg._opener.isConnected && dlg._opener.focus({ preventScroll: true }));
        }
        return;
      }
      const code = t.closest('[data-code-strip]');
      if (code) {
        tearCode(code);
        return;
      }
      const sh = t.closest('[data-share-strip]');
      if (sh && cfg.shareStrip !== false) shareCard(sh);
    });

    d.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const open = $$('[data-size-chip]').find((c) => !$('.size-chip__pop', c).hidden);
      if (open) chipClose(open, true);
    });

    d.addEventListener('submit', (e) => {
      const form = e.target.closest && e.target.closest('[data-look-form]');
      if (!form) return;
      e.preventDefault();
      const dlg = form.closest('dialog');
      const err = $('[data-look-error]', dlg || form);
      const ids = lookItems(form);
      $$('.fringe.is-invalid', form).forEach((f) => f.classList.remove('is-invalid'));
      if (!ids.length) {
        const f = $('[data-fringe]', form);
        if (f) {
          f.classList.add('is-invalid');
          const i = $('.fringe__input', f);
          if (i) i.focus();
        }
        say(S.chooseSize);
        return;
      }
      if (!ams()) return;
      const btn = $('[data-look-submit]', form);
      btn.classList.add('is-loading');
      btn.disabled = true;
      if (err) err.hidden = true;
      ams()
        .cart.addEach(
          ids.map((id) => ({ id: Number(id), quantity: 1 })),
          { source: form }
        )
        .then((r) => {
          if (!r.failed.length) {
            if (dlg) dlg.close();
            return;
          }
          r.failed.forEach((f) => {
            const i = $$('.fringe__input:checked', form).find((x) => x.value === String(f.item.id));
            const fr = i && i.closest('[data-fringe]');
            if (fr) fr.classList.add('is-invalid');
          });
          if (err) {
            err.textContent = fill(S.lookPartial, { COUNT: r.failed.length });
            err.hidden = false;
          }
          say(fill(S.lookPartial, { COUNT: r.failed.length }));
        })
        .catch(() => {
          if (err) {
            err.textContent = S.cartError || '';
            err.hidden = false;
          }
        })
        .finally(() => {
          btn.classList.remove('is-loading');
          btn.disabled = false;
        });
    });

    /* peel intent → hydrate the under-layer; drag from the dog-ear */
    const intent = (e) => {
      const ear = e.target.closest && e.target.closest('[data-peel]');
      if (ear) hydrate(ear.closest('[data-poster]'));
    };
    d.addEventListener('pointerover', intent);
    d.addEventListener('focusin', intent);
    d.addEventListener('pointerdown', (e) => {
      const ear = e.target.closest && e.target.closest('[data-peel]');
      if (!ear || e.button > 0 || peeling) return;
      e.preventDefault();
      const poster = ear.closest('[data-poster]');
      try {
        ear.setPointerCapture(e.pointerId);
      } catch (err) {}
      const h = startPeel(poster, e.clientX, e.clientY);
      if (h) drag = { poster, h, id: e.pointerId, x: e.clientX, y: e.clientY, moved: false };
    });
    d.addEventListener('pointermove', (e) => {
      if (!drag || e.pointerId !== drag.id) return;
      if (Math.hypot(e.clientX - drag.x, e.clientY - drag.y) > 6) drag.moved = true;
      if (!drag.moved) return;
      const p = drag.h.move(e.clientX, e.clientY) || 0;
      fade(drag.poster, p > 0.15 ? clamp(1 - (p - 0.15) / 0.35, 0, 1) : 1);
    });
    const up = (e) => {
      if (!drag || e.pointerId !== drag.id) return;
      const { poster, h, moved } = drag;
      drag = null;
      if (e.type === 'pointercancel') {
        h.cancel();
        finish(poster, false);
        return;
      }
      (moved ? h.end() : h.auto()).then((torn) => finish(poster, torn));
    };
    d.addEventListener('pointerup', up);
    d.addEventListener('pointercancel', up);

    /* flight + stubs */
    d.addEventListener('cart:add-start', (e) => {
      const src = e.detail && e.detail.source;
      if (!src || !src.querySelectorAll) return;
      dropFlight(src);
      const ins = $$('.fringe__input:checked', src).slice(0, 6);
      const els = calm()
        ? []
        : ins.map((i) => {
            const lab = i.nextElementSibling;
            const r = lab.getBoundingClientRect();
            const f = d.createElement('div');
            f.className = 'strip-flight';
            f.setAttribute('aria-hidden', 'true');
            f.dataset.id = i.value;
            f.style.cssText = `left:${r.left}px;top:${r.top}px;width:${r.width}px;height:${r.height}px`;
            const c = lab.cloneNode(true);
            c.removeAttribute('for');
            c.classList.remove('is-yours', 'is-pocketed');
            f.append(c);
            d.body.append(f);
            return f;
          });
      flights.set(src, { els, ins: ins.map((i) => ({ id: i.value, size: i.dataset.size })) });
    });
    d.addEventListener('cart:updated', (e) => {
      const det = e.detail || {};
      const f = det.source && flights.get(det.source);
      loadPocket();
      if (!f) return;
      flights.delete(det.source);
      const ok = new Set((det.items || []).map((i) => String(i.id)));
      f.els.forEach((el) => (ok.has(el.dataset.id) ? fly(el) : el.remove()));
      const got = f.ins.filter((i) => ok.has(i.id)).map((i) => i.size);
      if (got.length) say(fill(S.addedSr, { SIZE: got.join(', ') }));
    });
    d.addEventListener('cart:error', (e) => e.detail && dropFlight(e.detail.source));

    d.addEventListener('wall:countdown-end', (e) => {
      const sec = e.target.closest('[data-countdown-section]');
      if (!sec) return;
      if (sec.dataset.ended === 'hide') {
        sec.hidden = true;
        return;
      }
      sec.classList.add('is-ended');
      const band = $('.countdown-band', sec);
      if (band) band.classList.add('is-torn');
      const p = $('[data-poster]', sec);
      if (p) p.style.setProperty('--wet', '1');
      const link = $('[data-countdown-link]', sec);
      if (link) link.hidden = false;
      say(S.countdownEnded);
    });

    d.addEventListener('shopify:section:load', (e) => init(e.target));
    d.addEventListener('shopify:section:unload', (e) => destroy(e.target));
    w.addEventListener('pageshow', (e) => e.persisted && loadPocket());
  };

  const init = (scope = d) => {
    if (!scope || !scope.querySelectorAll) return;
    wet(scope);
    paint(scope);
    all('[data-wall]', scope).forEach(initView);
    initPaste(scope);
    all('[data-countdown]', scope).forEach(countdown);
    all('[data-layers]', scope).forEach(initLayers);
  };

  w.Wall = { init, destroy, size: { get: size.get, set: size.set, clear: size.clear } };

  const boot = () => {
    if (design()) root.classList.add('is-design-mode');
    watchConsent();
    bind();
    init(d);
    if (ams()) loadPocket();
    else d.addEventListener('ams:ready', loadPocket, { once: true });
  };
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
